import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Card } from "../../primitives/card";
import { Button } from "../../primitives/button";
import { Textarea } from "../../primitives/textarea";
import { MessageSquare, GitCommitHorizontal } from "lucide-react";
import type { ActivityRecord, AddIssueNote } from "@shared-core/data/api-types";

interface IssueActivityTabProps {
  activities: ActivityRecord[];
  /** Investigate tier sees investigation-content activity; report tier does not. */
  canSeeRestricted: boolean;
  posting: boolean;
  onAddNote: (data: AddIssueNote) => void;
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return formatDistanceToNow(d, { addSuffix: true });
}

export function IssueActivityTab({
  activities,
  canSeeRestricted,
  posting,
  onAddNote,
}: IssueActivityTabProps) {
  const [note, setNote] = useState("");

  const visible = activities.filter((a) => canSeeRestricted || !a.restricted);
  const ordered = [...visible].reverse();

  function submit() {
    const body = note.trim();
    if (!body) return;
    onAddNote({ note_body: body });
    setNote("");
  }

  return (
    <div className="space-y-5">
      <Card className="p-6 space-y-3">
        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note…"
          className="min-h-20"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={submit} disabled={posting || !note.trim()}>
            {posting ? "Posting…" : "Add note"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        {ordered.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">
            No activity yet
          </p>
        ) : (
          <ol className="space-y-4">
            {ordered.map((entry) => (
              <li key={entry.id} className="flex gap-3">
                <div className="mt-0.5 text-muted-foreground">
                  {entry.kind === "note" ? (
                    <MessageSquare className="w-4 h-4" />
                  ) : (
                    <GitCommitHorizontal className="w-4 h-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm">
                    <span className="font-medium">{entry.actor_id}</span>{" "}
                    <span className="text-muted-foreground">
                      {entry.summary}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {relativeTime(entry.at)}
                  </div>
                  {entry.note_body && (
                    <p className="mt-1.5 rounded bg-muted p-3 text-sm whitespace-pre-wrap">
                      {entry.note_body}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </div>
  );
}
